import dayjs from 'dayjs';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { uniq } from 'ramda';
import React from 'react';

import { useUserContext } from 'App/contexts/UserContext';
import useFetchPatientIdentifiers from 'App/hooks/useFetchPatientIdentifiers';
import useFetchPatientOtherMedications from 'App/hooks/useFetchPatientOtherMedications';
import useFetchPatientPrescriptions from 'App/hooks/useFetchPatientPrescriptions';
import { dateOnlySlashesFormat, dateTimeSlashesFormat, formatDate, formatUtcDate } from 'App/services/DateService';
import PESService from 'App/services/PESService';
import { branchSettingsPropType, patientPropType } from 'Shared/types';
import useFetchMedications from '../hooks/useFetchMedications';
import useFetchPatientAllergies from '../hooks/useFetchPatientAllergies';
import { medicationAssociationPropType, questionGroupPropType, questionPropType } from '../types';
import EhrEncounterNoteQuestion from './EhrEncounterNoteQuestion';
import { ReviewSectionStyle } from 'App/components/EHR/styles';

const parseHiddenSections = (ehrNotesHiddenSections) => {
    try {
        return JSON.parse(ehrNotesHiddenSections);
    } catch (_e) {
        return {};
    }
};

const EhrEncounterReviewContent = ({
    patient,
    allQuestions,
    questionGroups,
    branchSettings,
    medicationAssociationsList,
}) => {
    const { values } = useFormikContext();
    const user = useUserContext();

    const { data: patientIdentifiers } = useFetchPatientIdentifiers(patient.id);
    const primaryIdentifier = (!patientIdentifiers ? [] : Object.values(patientIdentifiers)).find(
        (i) => i.identifierType === branchSettings.ehrPrimaryIdType
    );
    const patientPrimaryIdentifier = primaryIdentifier?.identifier;

    const activityDateQuestion = Object.values(values).find((questionValue) => questionValue.name === 'activity_date');

    const { data: prescriptions } = useFetchPatientPrescriptions(patient.id);
    const prescriptionsWithPhysicians = Object.values(prescriptions || {}).filter(
        (p) =>
            !!p.physician &&
            !!medicationAssociationsList.find(
                (m) => m.rx?.rxId === p.id || m.medication.medicationId === p.medication.id
            )
    );
    const physiciansList = uniq(
        prescriptionsWithPhysicians.map((p) => `${p.physician.firstName} ${p.physician.lastName}`)
    );

    const activityMedications = uniq(
        medicationAssociationsList.map((med) => med.displayName || med.medication.medicationName)
    );

    const { data: medications } = useFetchMedications(patient.id);

    const therapyNamesList = uniq(
        medicationAssociationsList.map((medAssociation) => {
            return medications?.[medAssociation.patientMedicationId]?.therapy.name;
        })
    );

    const { data: otherMedications = [] } = useFetchPatientOtherMedications(patient.id);
    const otherMedicationsList = Object.values(otherMedications);

    const { data: allergies = [] } = useFetchPatientAllergies(patient.id);

    const hiddenSections = parseHiddenSections(branchSettings.ehrNotesHiddenSections);

    return (
        <div>
            <div>
                Completed on {dayjs().format(dateTimeSlashesFormat)}, by {user.first_name} {user.last_name}
                {user.suffix && `, ${user.suffix}`}
            </div>

            {!hiddenSections.patientInfo && (
                <ReviewSectionStyle>
                    <strong>Patient</strong>
                    <div>
                        Patient Name: {patient.lastName}, {patient.firstName}{' '}
                        {patient.middleName?.charAt(0).toUpperCase()}
                    </div>
                    <div>DOB: {formatUtcDate({ date: patient.birthDate, format: dateOnlySlashesFormat })}</div>
                    <div data-testid="patient-ehr-id">EHR ID: {patientPrimaryIdentifier}</div>
                </ReviewSectionStyle>
            )}

            {!hiddenSections.activityDate && (
                <ReviewSectionStyle>
                    <strong>Activity Date </strong>
                    {formatDate(activityDateQuestion?.response)}
                </ReviewSectionStyle>
            )}

            {!hiddenSections.providers && (
                <ReviewSectionStyle>
                    <strong>Providers </strong>
                    {!physiciansList.length ? (
                        '--'
                    ) : (
                        <ul id="ehr-provider-list">
                            {physiciansList.map((p) => (
                                <li key={p}>{p}</li>
                            ))}
                        </ul>
                    )}
                </ReviewSectionStyle>
            )}

            {!hiddenSections.medications && (
                <ReviewSectionStyle>
                    <strong>Activity Medications </strong>
                    {!activityMedications.length ? (
                        '--'
                    ) : (
                        <ul>
                            {activityMedications.map((med) => (
                                <li key={med}>{med}</li>
                            ))}
                        </ul>
                    )}
                </ReviewSectionStyle>
            )}

            {!hiddenSections.encounterNotes && (
                <ReviewSectionStyle>
                    <strong>Encounter Notes</strong>
                    <div>
                        {patient.firstName} {patient.lastName} is a {PESService.getAge(patient.birthDate)}-year-old{' '}
                        {branchSettings.ehrNoteCustomEncounterNoteContent
                            ? `patient ${branchSettings.ehrNoteCustomEncounterNoteContent}`
                            : `${
                                  patient.gender
                              } being followed by the clinical pharmacist for medication monitoring of ${
                                  !therapyNamesList.length ? '--' : therapyNamesList.join(', ')
                              }. This patient's current med list, allergies, and vaccination status have been reviewed and updated as appropriate.`}
                    </div>
                </ReviewSectionStyle>
            )}

            {!hiddenSections.currentMedications && (
                <ReviewSectionStyle>
                    <strong>Other Medications </strong>
                    {!otherMedicationsList.length ? (
                        '--'
                    ) : (
                        <ul>
                            {otherMedicationsList.map((otherMedication) => (
                                <li key={otherMedication.id}>{otherMedication.text}</li>
                            ))}
                        </ul>
                    )}
                </ReviewSectionStyle>
            )}

            {!hiddenSections.allergies && (
                <ReviewSectionStyle>
                    <strong>Allergies </strong>
                    {!allergies.length ? (
                        '--'
                    ) : (
                        <ul>
                            {allergies.map((allergy) => (
                                <li key={allergy}>{allergy}</li>
                            ))}
                        </ul>
                    )}
                </ReviewSectionStyle>
            )}

            {!hiddenSections.careDetails && (
                <ReviewSectionStyle>
                    <div>
                        <strong>Care Details</strong>
                    </div>
                    {Object.values(allQuestions)
                        .filter((q) => !q.parentQuestionId)
                        .sort((a, b) => {
                            if (a.attributes.group_id === null && b.attributes.group_id !== null) {
                                return -1;
                            }
                            if (b.attributes.group_id === null && a.attributes.group_id !== null) {
                                return 1;
                            }
                            const aGroup = questionGroups.find((g) => g.id === Number(a.attributes.group_id));
                            const bGroup = questionGroups.find((g) => g.id === Number(b.attributes.group_id));
                            if (aGroup?.groupOrder > bGroup?.groupOrder) {
                                return 1;
                            }
                            if (aGroup?.groupOrder < bGroup?.groupOrder) {
                                return -1;
                            }
                            return a.attributes.position - b.attributes.position;
                        })
                        .map((question) => (
                            <EhrEncounterNoteQuestion
                                key={question.questionId}
                                question={question}
                                patient={patient}
                                branchSettings={branchSettings}
                            />
                        ))}
                </ReviewSectionStyle>
            )}

            {!hiddenSections.identifiedConcerns && (
                <ReviewSectionStyle>
                    <strong>Identified Concerns </strong>
                    {!values.newSummaryNoteClinicalIssues?.length ? (
                        '--'
                    ) : (
                        <ul>
                            {values.newSummaryNoteClinicalIssues.map((clinicalIssue) => (
                                <li key={clinicalIssue.valueName}>{clinicalIssue.valueName}</li>
                            ))}
                        </ul>
                    )}
                </ReviewSectionStyle>
            )}

            {!hiddenSections.summaryNotes && (
                <ReviewSectionStyle>
                    <strong>Summary Notes</strong>
                    <div>{values.newSummaryNote || '--'}</div>
                </ReviewSectionStyle>
            )}
        </div>
    );
};

EhrEncounterReviewContent.propTypes = {
    patient: patientPropType.isRequired,
    allQuestions: PropTypes.objectOf(questionPropType).isRequired,
    questionGroups: PropTypes.arrayOf(questionGroupPropType).isRequired,
    branchSettings: branchSettingsPropType.isRequired,
    medicationAssociationsList: PropTypes.arrayOf(medicationAssociationPropType).isRequired,
};

export default EhrEncounterReviewContent;
